import { OnInit, Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  ApiGatewayService,
  EcaSpinner,
  WindowService,
} from '@drc-eca/eca-components-lib';
import { LayoutDirective, LayoutAlignDirective } from '@ngbracket/ngx-layout/flex';
import { ProductContextService } from '../services/product-context.service';

@Component({
  selector: 'app-short-token-redirect',
  templateUrl: './short-token-redirect.component.html',
  styleUrls: ['./short-token-redirect.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [LayoutDirective, LayoutAlignDirective, EcaSpinner],
})
export class ShortTokenRedirectComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private apiGatewayService = inject(ApiGatewayService);
  private productContextService = inject(ProductContextService);
  private windowService = inject(WindowService);
  public dialog = inject(MatDialog);

  constructor() {}

  getRoute(url: string) {
    const route = url.split('eca-portal-v2-ui/')[1];
    const n = route.lastIndexOf('/');
    return route.slice(0, n) + route.slice(n).replace('/', '');
  }

  async ngOnInit() {
    this.productContextService.setSelectedProductCode('');
    // The email will be similar to http://cdn-app-dev.drcedirect.com/all/eca-portal-v2-ui/user-email/{ShortUrlCode}?productCode={PRODUCTCODE}
    // The Portal will reroute to   http://cdn-app-dev.drcedirect.com/all/eca-portal-v2-ui/#/user-email/{ShortUrlCode}?productCode={PRODUCTCODE}
    // Before this Component is executed so we can't use native URL
    // Might not include productCode=PRODUCTCODE
    const productCode = this.windowService.location.href.split('productCode=')[1]?.split('&')[0];
    if (productCode) {
      this.productContextService.setSelectedProductCode(productCode);
    }

    const { shortToken } = await firstValueFrom(this.route.params.pipe(take(1)));
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const urlBase = `eca-security-service/${apiConfig}/v0/email/`;

    try {
      const response: any = await firstValueFrom(
        this.apiGatewayService.get(`${urlBase}shortUrl/${shortToken}`),
      );
      // This section is mostly redundant
      if (response.redirectUrl.includes('productCode')) {
        // The response is a properly structured URL so we can use native URL
        // http://cdn-app-dev.drcedirect.com/all/eca-portal-v2-ui/reset-password/?token={JWT}&productCode={PRODUCTCODE}
        const responseUrl = new URL(response.redirectUrl);
        const productCode = responseUrl.searchParams.get('productCode') as string;
        this.productContextService.setSelectedProductCode(productCode);
      }
      // Check url freshness for password resets
      if (response.redirectUrl.includes('reset-password')) {
        const freshnessResponse: any = await firstValueFrom(
          this.apiGatewayService.get(`${urlBase}checkShortUrlFresh/${shortToken}`),
        );
        if (freshnessResponse.shortUrlStatus !== 'fresh') {
          const urlBadText =
            'This link has expired. You will not be able to update your password with this link. Please check your email inbox for a more recent reset-password link, or try clicking the reset password link again.';
          throw new HttpErrorResponse({ status: 410, statusText: urlBadText });
        }
      }
      const targetRoute = this.getRoute(response.redirectUrl);

      await this.router.navigateByUrl(targetRoute);
    } catch (e) {
      const err = e as any;
      const expiredText = 'This link has expired. Please click the reset password link again.';
      const errorPageText = err.status === 404 ? expiredText : err.statusText;
      history.pushState({}, '', location.origin + location.pathname + '#/forgot-password');
      await this.router.navigate([`/error/${err.status}`], {
        queryParams: { desc: errorPageText },
      });
    }
  }
}
